import { Tooltip, Typography } from '@mui/material';
import { Metric } from '@strength/domain';
import {
  SetMetricMutationData,
  WorkoutSetMetricOverwritten,
} from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { resolveSlotWorkout } from '../cross-block-resolver';
import { ActualWorkout, WorkoutSummaryActualsMutationFns } from '../types';
import { BaseWorkoutSlot } from './BaseWorkoutSlot';
import { ProvenanceTooltipLines } from './ProvenanceTooltipLines';
import { SOURCE_DOT_DIAMETER_PX, DisabledSlot } from './common';
import { getRepsValueForMetricKeyword } from './workout-metric-utils';

interface ActualSlotProps {
  x: number;
  y: number;
  workout: ActualWorkout | undefined;
  metricKeyword: string;
  mutationFns: WorkoutSummaryActualsMutationFns;
  getMetric: (metricId: string) => Metric;
  t: I18nTranslator;
  readOnly?: boolean;
}

export const ActualSlot = ({
  x,
  y,
  metricKeyword,
  workout,
  mutationFns,
  getMetric,
  t,
  readOnly = false,
}: ActualSlotProps) => {
  // Resolve the bowling set from the merged workout. When the athlete has no
  // actuals layer yet, the merged workout still carries the prescription's
  // set/metric shape — we need that to mount an editable cell that creates a
  // new actual on commit.
  const resolved = workout
    ? resolveSlotWorkout(workout.workout, workout.crossBlock, workout.hasActual)
    : undefined;
  // Prefer the actual-merged workout when available (cross-block overlays).
  // Falls back to the prescription workout so the cell structure still mounts
  // correctly for editable slots that have no actuals yet.
  const workoutForRender = resolved?.actualWorkout ?? resolved?.workout;
  const isOverlay = resolved?.isOverlay ?? false;

  const {
    value: repsValue,
    metric,
    workoutExercise,
    workoutSet,
  } = getRepsValueForMetricKeyword(metricKeyword, workoutForRender);

  if (metric && workoutExercise && workoutSet && workout) {
    const context = workout.context;

    // Get the data source for the metric if it is external. dataSource is
    // only present on a real actuals layer (cricket webhook, Catapult), so
    // when hasActual is false this is undefined and the cell stays editable.
    // For cross-block overlays, the rendered workout is the source block's
    // merged workout, so dataSource still reflects that block's external feed.
    const setMetric = workoutSet.metrics.find(
      (m) => m.metricId === metric.metricId,
    );
    const dataSource = setMetric?.dataSource;

    // Everyone who can edit this grid is a practitioner — tablet-only users
    // are redirected to the tablet actuals route and athletes record on
    // mobile — so every edit commits as 'Manual Override' (rank 0 in
    // ACTUALS_SOURCE_RANKING). Stamping only when overwriting a visible feed
    // value left a hole: a practitioner entry made *before* the Catapult /
    // Player Entered sync landed saved as a plain actual (null rank) and the
    // later feed entry displaced it on merge. Re-editing a Manual Override
    // still updates it in place via the builder.
    const commitDataSource: 'Manual Override' = 'Manual Override';
    const onEditSetMetric = (
      workoutId: string,
      workoutSetId: string,
      data: SetMetricMutationData,
    ) => {
      mutationFns.onEditSetMetric(context, workoutId, workoutSetId, {
        ...data,
        dataSource: commitDataSource,
      });
    };

    // Only display a value when the rendering workout (current block or
    // cross-block source) has actuals — the resolver folds both sources
    // into one flag so we don't branch here.
    const shouldShowValue = resolved?.hasActual ?? false;
    const displayValue = shouldShowValue ? repsValue ?? '' : '';

    // Rows written before provenance existed have neither field — hide the
    // hover entirely for them rather than showing partial provenance.
    const hasProvenance = !!(setMetric?.enteredBy || setMetric?.enteredOn);

    // The hover is data provenance, so it only makes sense on cells with
    // actuals data — empty (editable, no-actual) cells show no icon. Rows
    // written before provenance existed (no enteredBy/enteredOn) show no
    // hover for a Manual Override dataSource, same as before.
    const tooltipIcon =
      shouldShowValue && dataSource && dataSource !== 'Manual Override'
        ? getSourceIconForActualSlot({
            enteredBy: setMetric?.enteredBy,
            enteredOn: setMetric?.enteredOn,
            dataSource,
            overwritten: setMetric?.overwritten,
            crossTeamName: resolved?.crossTeamName,
            t,
          })
        : shouldShowValue && hasProvenance
          ? getSourceIconForActualSlot({
              enteredBy: setMetric?.enteredBy,
              enteredOn: setMetric?.enteredOn,
              dataSource,
              overwritten: setMetric?.overwritten,
              crossTeamName: resolved?.crossTeamName,
              t,
            })
          : undefined;

    const disabled = isOverlay;

    return BaseWorkoutSlot(
      x,
      y,
      displayValue,
      metric,
      workoutExercise,
      workoutSet,
      onEditSetMetric,
      getMetric,
      !!readOnly || disabled,
      true, // disable context menu for actual slots
      tooltipIcon,
    );
  } else {
    return DisabledSlot(x, y);
  }
};

const getSourceIconForActualSlot = ({
  dataSource,
  enteredBy,
  enteredOn,
  overwritten,
  crossTeamName,
  t,
}: {
  dataSource?: string;
  enteredBy?: string;
  enteredOn?: string;
  overwritten?: WorkoutSetMetricOverwritten;
  crossTeamName?: string;
  t: I18nTranslator;
}) => {
  return (
    <Tooltip
      placement="bottom"
      arrow
      title={
        <TooltipContent
          dataSource={dataSource}
          enteredBy={enteredBy}
          enteredOn={enteredOn}
          overwritten={overwritten}
          crossTeamName={crossTeamName}
          t={t}
        />
      }
    >
      <span
        className="block bg-blue-600 shrink-0 rounded-full"
        style={{
          width: SOURCE_DOT_DIAMETER_PX,
          height: SOURCE_DOT_DIAMETER_PX,
        }}
        aria-label={
          dataSource
            ? t('dataSource')
            : enteredBy
              ? t('enteredBy')
              : t('enteredOn')
        }
      />
    </Tooltip>
  );
};

/**
 * Display label for a data source. 'Player Entered' is the stored enum name
 * but the product language is "Athlete Entered"; a Manual Override by a real
 * user (enteredBy is a profileId) is a "Practitioner Entered" value. AMS-fed
 * sources keep their feed name (Catapult, Match Stats).
 */
const dataSourceLabel = (
  dataSource: string,
  enteredBy: string | undefined,
  t: I18nTranslator,
): string => {
  if (dataSource === 'Player Entered') return t('athleteEntered');
  if (dataSource === 'Manual Override') {
    // Overrides carry a profileId when entered by a practitioner; feeds (AMS)
    // or legacy rows without provenance fall back to the translated source name.
    if (enteredBy && enteredBy !== 'AMS') return t('practitionerEntered');
    return t('manualOverride');
  }
  return dataSource;
};

const TooltipContent = ({
  dataSource,
  enteredBy,
  enteredOn,
  overwritten,
  crossTeamName,
  t,
}: {
  dataSource?: string;
  enteredBy?: string;
  enteredOn?: string;
  overwritten?: WorkoutSetMetricOverwritten;
  crossTeamName?: string;
  t: I18nTranslator;
}) => {
  // Labels grey, values white.
  const Label = ({ children }: { children: string }) => (
    <Typography color="grey" variant="caption">
      {children}:{' '}
    </Typography>
  );
  const Value = ({ children }: { children: React.ReactNode }) => (
    <Typography color="white" variant="caption" component="span">
      {children}
    </Typography>
  );
  return (
    <div className="flex flex-col items-start gap-0.5">
      {crossTeamName && (
        <Typography color="grey" variant="caption">
          <Label>{t('teamEntered')}</Label>
          <Value>{crossTeamName}</Value>
        </Typography>
      )}
      {/* Order: Latest Source, Entered By, Entered On, overwritten source.
          No standalone date line — Entered On already carries it. */}
      <ProvenanceTooltipLines
        enteredBy={enteredBy}
        enteredOn={enteredOn}
        hideDate
        sourceLine={
          dataSource
            ? {
                label: t('latestSource'),
                value: dataSourceLabel(dataSource, enteredBy, t),
              }
            : undefined
        }
        t={t}
      />
      {/* Only render when the replaced entry had a named source — an
          internally-entered value (no dataSource) has no label to show, so
          the merge can retain it but there is nothing meaningful to display. */}
      {overwritten?.dataSource && (
        <Typography color="grey" variant="caption">
          <Label>{t('source')}</Label>
          <Value>
            {dataSourceLabel(overwritten.dataSource, overwritten.enteredBy, t)}{' '}
            ({overwritten.input})
          </Value>
        </Typography>
      )}
    </div>
  );
};
